import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";

const CENTRAL_PARK_MAP_EMBED =
	"https://www.google.com/maps/d/embed?mid=1GRimRlIoHFotqlB_bBUejWqf-f8XaIE&ehbc=2E312F";

// Template84: Día 4 Mapa Central Park (Page17). Contenido desde page.
const Template84 = ({
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
	onMapFullscreenChange,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;
	const [loaded, setLoaded] = useState(navigator.onLine);
	const [fullscreen, setFullscreen] = useState(false);
	const [isOnline, setIsOnline] = useState(navigator.onLine);

	const getImageSrc = (id) => {
		if (page?.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found && found.src) return found.src;
		}
		return undefined;
	};
	const getCanvasImageUrl = (id) => {
		if (page?.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const title = page?.texts?.[0]?.content;
	const titleShort = page?.texts?.[1]?.content;
	const yellowBici = page?.texts?.[2]?.content;
	const mapDescription = page?.texts?.[3]?.content;
	const mapUrl = page?.maps?.[0] || CENTRAL_PARK_MAP_EMBED;
	const mapaOfflineSrc = getImageSrc("mapa-offline");
	const biciSrc = getImageSrc("bici");
	const logoSrc = getCanvasImageUrl("logo");
	const displayTitle = isSmallScreen && titleShort ? titleShort : title;

	useEffect(() => {
		const handleOnline = () => {
			setIsOnline(true);
			setLoaded(true);
		};
		const handleOffline = () => {
			setIsOnline(false);
			setLoaded(true);
		};
		window.addEventListener("online", handleOnline);
		window.addEventListener("offline", handleOffline);
		return () => {
			window.removeEventListener("online", handleOnline);
			window.removeEventListener("offline", handleOffline);
		};
	}, []);

	useEffect(() => {
		const style = document.createElement("style");
		style.innerHTML = `
			@keyframes spin {
				0% { transform: rotate(0deg); }
				100% { transform: rotate(360deg); }
			}
		`;
		document.head.appendChild(style);
		return () => document.head.removeChild(style);
	}, []);

	if (fullscreen) {
		return (
			<Box
				sx={{
					position: "fixed",
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					backgroundColor: "#fff",
					zIndex: 9999,
					display: "flex",
					flexDirection: "column",
				}}
			>
				<Box
					sx={{
						padding: "10px",
						textAlign: "right",
						backgroundColor: "#59c3d8",
					}}
				>
					<Box
						component="button"
						onClick={() => {
							setFullscreen(false);
							onMapFullscreenChange?.(false);
						}}
						sx={{
							backgroundColor: "#fff",
							color: "#59c3d8",
							border: "none",
							borderRadius: "8px",
							padding: "6px 12px",
							fontWeight: 600,
							cursor: "pointer",
							fontFamily: fontFamily || '"Poppins", sans-serif',
						}}
					>
						← Volver a la guía
					</Box>
				</Box>
				<Box
					component="iframe"
					title="Mapa grande Central Park"
					src={mapUrl}
					width="100%"
					height="100%"
					sx={{ border: 0, flex: 1 }}
					allowFullScreen
					loading="lazy"
				/>
			</Box>
		);
	}

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				backgroundColor: "#f7f7f7",
				position: "relative",
				"& *": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				},
			}}
		>
			<div
				data-block-id="header"
				style={{
					backgroundColor: headerBackgroundColor || "#59c3d8",
					color: headerTextColor || "#fff",
					textAlign: "center",
					padding: "15px 0",
					fontFamily: fontFamily || '"Poppins", sans-serif',
					fontSize: "14px",
					fontWeight: 700,
					flexShrink: 0,
					position: "sticky",
					top: 0,
					zIndex: 99999,
				}}
			>
				{displayTitle}
				{logoSrc && (
					<img
						src={logoSrc}
						alt="logo"
						style={{
							width: isSmallScreen ? 66 : 80,
							position: "absolute",
							right: 2,
							top: 18,
							zIndex: 999999,
						}}
					/>
				)}
			</div>

			<div
				data-block-id="map"
				style={{
					flex: 1,
					marginTop: "-70px",
					position: "relative",
					minHeight: 340,
					display: "flex",
					flexDirection: "column",
					minWidth: 0,
				}}
			>
				{!loaded && isOnline && (
					<div
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							position: "absolute",
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							zIndex: 1,
						}}
					>
						<div
							style={{
								width: 20,
								height: 20,
								border: "4px solid #ccc",
								borderTop: "4px solid #59c3d8",
								borderRadius: "50%",
								animation: "spin 1s linear infinite",
							}}
						/>
					</div>
				)}
				{isOnline && (
					<Box
						component="button"
						onClick={() => {
							setFullscreen(true);
							onMapFullscreenChange?.(true);
						}}
						sx={{
							position: "absolute",
							top: 80,
							right: 10,
							backgroundColor: "#59c3d8",
							color: "#fff",
							padding: "8px 10px",
							fontSize: "11px",
							borderRadius: "8px",
							boxShadow: "1px 1px 2px rgba(0,0,0,0.2)",
							border: "none",
							cursor: "pointer",
							fontFamily: fontFamily || '"Poppins", sans-serif',
						}}
					>
						Ver mapa grande
					</Box>
				)}
				<div style={{ flex: 1, minHeight: 0, position: "relative" }}>
					{isOnline ? (
						<Box
							component="iframe"
							title="Mapa Central Park"
							src={mapUrl}
							width="100%"
							height="100%"
							sx={{
								border: 0,
								display: "block",
								position: "absolute",
								top: 0,
								left: 0,
								right: 0,
								bottom: 0,
							}}
							loading="lazy"
							onLoad={() => setLoaded(true)}
						/>
					) : mapaOfflineSrc ? (
						<img
							src={mapaOfflineSrc}
							alt="Mapa Central Park (offline)"
							style={{
								width: "100%",
								height: "100%",
								objectFit: "cover",
								position: "absolute",
								top: 0,
								left: 0,
								right: 0,
								bottom: 0,
							}}
						/>
					) : null}
				</div>
				{biciSrc && (
					<img
						src={biciSrc}
						alt="bici en Central Park"
						style={{
							width: 120,
							position: "absolute",
							bottom: isSmallScreen ? 0 : -10,
							left: -18,
						}}
					/>
				)}
				{yellowBici && (
					<div
						style={{
							position: "absolute",
							bottom: 40,
							left: 80,
							backgroundColor: "#fff8db",
							border: "2px solid #f5c400",
							borderRadius: "16px 16px 16px 0px",
							padding: "10px 12px",
							fontSize: 11,
							fontFamily: '"Poppins", sans-serif',
							color: "#444",
							boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
							maxWidth: 200,
						}}
					>
						{yellowBici.includes("!") ? (
							<>
								<strong>
									{yellowBici.substring(0, yellowBici.indexOf("!") + 1)}
								</strong>
								<br />
								{yellowBici.substring(yellowBici.indexOf("!") + 1).trim()}
							</>
						) : (
							yellowBici
						)}
					</div>
				)}
			</div>

			{mapDescription && (
				<div
					data-block-id="text-1"
					style={{
						padding: "12px 16px",
						fontFamily: '"Poppins", sans-serif',
						fontSize: 12,
						backgroundColor: "#fff",
						color: "#333",
					}}
				>
					{mapDescription}
				</div>
			)}
		</Box>
	);
};

export default Template84;
